import React from 'react'
import styled, { css } from 'styled-components'
import { cardStyle, font, ink, line, radius, shadow, surface, transition } from 'features/dashboard/tokens'
import { RAG, scoreBandFor } from 'features/dashboard/charts/scoreColour'

const toneStyles = {
  critical: RAG.critical,
  high: RAG.high,
  medium: RAG.medium,
  low: RAG.low,
  none: RAG.none,
}

const toneFor = item => {
  const band = scoreBandFor(item.score)
  if (band) return band.level
  return item.unmet >= 10 ? 'critical' : 'high'
}

const AttentionBand = ({ items = [] }) => {
  const clear = items.length === 0

  return (
    <Section aria-label='Needs your attention'>
      <Head>
        <h2>Needs your attention</h2>
        {!clear && <Count>{items.length}</Count>}
        <Note>{clear ? 'Nothing outstanding right now' : 'Resolve these first — statutory duties with missing evidence'}</Note>
      </Head>

      {clear ? (
        <Clear role='status'>
          <ClearGlyph aria-hidden='true'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M20 6 9 17l-5-5' />
            </svg>
          </ClearGlyph>
          <div>
            <ClearTitle>You’re all caught up</ClearTitle>
            <ClearText>No portfolio or estate has unmet statutory obligations. New issues will appear here first.</ClearText>
          </div>
        </Clear>
      ) : (
        <Rows>
          {items.map(item => (
            <Row key={item.key} $tone={toneFor(item)}>
              <Stripe aria-hidden='true' />
              <Glyph aria-hidden='true'>{item.name ? item.name.charAt(0).toUpperCase() : '—'}</Glyph>
              <Details>
                <Name>
                  {item.name}
                  <Sub>{item.typeLabel}{item.meta ? ` · ${item.meta}` : ''}</Sub>
                </Name>
                <Issue>
                  <Dot aria-hidden='true' />
                  <b>{item.unmet}</b>&nbsp;unmet statutory obligation{item.unmet === 1 ? '' : 's'}
                </Issue>
              </Details>
              <Actions>
                <Btn type='button' onClick={item.onOpen}>Open</Btn>
                <Btn type='button' $solid onClick={item.onReview}>Review</Btn>
              </Actions>
            </Row>
          ))}
        </Rows>
      )}
    </Section>
  )
}

export default AttentionBand

const Section = styled.section`
  margin-bottom: 26px;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 13px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    font-size: ${font.heading};
    font-weight: 800;
    letter-spacing: -0.2px;
    color: ${ink.base};
  }
`

const Count = styled.span`
  font-size: ${font.small};
  font-weight: 800;
  color: ${RAG.critical.fg};
  background: ${RAG.critical.bg};
  border-radius: ${radius.pill};
  padding: 2px 10px;
  font-variant-numeric: tabular-nums;
`

const Note = styled.span`
  margin-left: auto;
  font-size: ${font.body};
  color: ${ink.mute};
  @media (max-width: 768px) {
    margin-left: 0;
    flex-basis: 100%;
  }
`

const Clear = styled.div`
  ${cardStyle}
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-color: ${RAG.low.fg}33;
  background: ${RAG.low.bg};
`

const ClearGlyph = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: ${radius.sm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${surface.card};
  color: ${RAG.low.fg};
`

const ClearTitle = styled.div`
  font-size: ${font.emphasis};
  font-weight: 800;
  color: ${RAG.low.fg};
`

const ClearText = styled.div`
  font-size: ${font.body};
  color: ${ink.soft};
  margin-top: 2px;
`

const Rows = styled.div`
  display: grid;
  gap: 10px;
`

const Stripe = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
`

const Glyph = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: ${radius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.emphasis};
  font-weight: 700;
`

const Details = styled.div`
  flex: 1;
  min-width: 0;
`

const Name = styled.div`
  font-size: ${font.emphasis};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
`

const Sub = styled.span`
  font-weight: 600;
  font-size: ${font.small};
  color: ${ink.mute};
  margin-left: 8px;
`

const Issue = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: ${font.body};
  font-weight: 700;
  b { font-variant-numeric: tabular-nums; }
`

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  margin-right: 7px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 620px) {
    width: 100%;
    button { flex: 1; }
  }
`

const Btn = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: ${radius.sm};
  font: inherit;
  font-weight: 700;
  font-size: ${font.body};
  cursor: pointer;
  white-space: nowrap;
  transition: ${transition};
  border: 1px solid ${({ $solid, theme }) => ($solid ? theme.colors.primary : line.strong)};
  background: ${({ $solid, theme }) => ($solid ? theme.colors.primary : surface.card)};
  color: ${({ $solid }) => ($solid ? '#fff' : ink.base)};

  &:hover {
    ${({ $solid, theme }) => ($solid
      ? 'filter: brightness(0.94);'
      : `border-color: ${theme.colors.secondary}; color: ${theme.colors.secondary};`)}
  }
`

const Row = styled.div`
  ${cardStyle}
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 14px 16px 14px 19px;
  overflow: hidden;
  transition: ${transition};

  &:hover {
    border-color: ${line.strong};
    box-shadow: ${shadow.lift};
  }

  @media (max-width: 620px) {
    flex-wrap: wrap;
  }

  ${({ $tone }) => {
    const t = toneStyles[$tone] || RAG.high
    return css`
      ${Stripe} { background: ${t.fg}; }
      ${Glyph} { background: ${t.bg}; color: ${t.fg}; }
      ${Issue} { color: ${t.fg}; }
    `
  }}
`
